/**
 * RoomDetail Component - View room details and manage games
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Card from '../common/Card';
import Button from '../common/Button';
import api from '../../services/api';
import { ROUTES } from '../../utils/constants';
import './Rooms.css';

const RoomDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRoom();
  }, [id]);

  const loadRoom = async () => {
    try {
      setLoading(true);
      const data = await api.getRoomById(id);
      setRoom(data.room);
    } catch (error) {
      toast.error('Error al cargar sala');
      navigate(ROUTES.ROOMS);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Estás seguro de eliminar esta sala?')) return;
    try {
      await api.deleteRoom(id);
      toast.success('Sala eliminada correctamente');
      navigate(ROUTES.ROOMS);
    } catch (error) {
      toast.error('Error al eliminar sala');
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Cargando sala...</p>
      </div>
    );
  }

  if (!room) {
    return (
      <Card title="Sala no encontrada">
        <p>La sala solicitada no existe.</p>
        <Button onClick={() => navigate(ROUTES.ROOMS)}>Volver a Salas</Button>
      </Card>
    );
  }

  return (
    <div className="room-detail">
      <Card
        title={room.name}
        subtitle={`Tipo: ${room.roomType || 'Pública'}`}
        actions={
          <div className="action-buttons">
            <Button variant="secondary" onClick={() => navigate(`${ROUTES.ROOMS}/${id}/edit`)}>
              Editar
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              Eliminar
            </Button>
          </div>
        }
      >
        <div className="detail-grid">
          <div className="detail-section">
            <h3>Información General</h3>
            <div className="detail-item">
              <span className="label">ID:</span>
              <span className="value">{room._id}</span>
            </div>
            <div className="detail-item">
              <span className="label">Estado:</span>
              <span className={`value status-badge status-${room.isActive ? 'active' : 'inactive'}`}>
                {room.isActive ? 'Activa' : 'Inactiva'}
              </span>
            </div>
            <div className="detail-item">
              <span className="label">Host:</span>
              <span className="value">{room.host?.username || 'N/A'}</span>
            </div>
            <div className="detail-item">
              <span className="label">Creada:</span>
              <span className="value">{new Date(room.createdAt).toLocaleString()}</span>
            </div>
          </div>

          <div className="detail-section">
            <h3>Configuración</h3>
            <div className="detail-item">
              <span className="label">Máx. Jugadores:</span>
              <span className="value">{room.maxPlayers}</span>
            </div>
            <div className="detail-item">
              <span className="label">Jugadores Actuales:</span>
              <span className="value">{room.players?.length || 0}</span>
            </div>
            <div className="detail-item">
              <span className="label">Costo de Entrada:</span>
              <span className="value">{room.entryFee} monedas</span>
            </div>
            <div className="detail-item">
              <span className="label">Premio:</span>
              <span className="value">{room.prizePool} monedas</span>
            </div>
            <div className="detail-item">
              <span className="label">Precio Cartón:</span>
              <span className="value">{room.cardPrice} monedas</span>
            </div>
            <div className="detail-item">
              <span className="label">Intervalo Llamada:</span>
              <span className="value">{room.callInterval / 1000}s</span>
            </div>
          </div>

          <div className="detail-section">
            <h3>Estadísticas</h3>
            <div className="detail-item">
              <span className="label">Juegos Totales:</span>
              <span className="value">{room.totalGamesPlayed || 0}</span>
            </div>
            <div className="detail-item">
              <span className="label">Juego Actual:</span>
              <span className="value">{room.currentGameStatus || 'Ninguno'}</span>
            </div>
          </div>

          {room.players && room.players.length > 0 && (
            <div className="detail-section full-width">
              <h3>Jugadores en Sala ({room.players.length})</h3>
              <div className="players-grid">
                {room.players.map(player => (
                  <div key={player._id} className="player-card">
                    <img src={player.avatarUrl || '/default-avatar.png'} alt={player.username} />
                    <span>{player.username}</span>
                    <span className={`status-dot ${player.isOnline ? 'online' : 'offline'}`}></span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="form-actions">
          <Button variant="secondary" onClick={() => navigate(ROUTES.ROOMS)}>
            Volver a Salas
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default RoomDetail;
